import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CrewCommandsStore } from "../core/sim/crew-commands.store";
import { MooringDrawerComponent } from "./mooring-drawer.component";
import { AnchorDrawerComponent } from "./anchor-drawer.component";
import { FendersDrawerComponent } from "./fenders-drawer.component";
import { CrewDrawerComponent } from "./crew-drawer.component";
import { MastDrawerComponent } from "./mast-drawer.component";

type CrewPanel = "mooring" | "anchor" | "fenders" | "crew" | "mast" | null;

/** Vertical layout constants for the icon rail (used to anchor popovers). */
const RAIL_BOTTOM = 12;
const BUTTON_SIZE = 44;
const RAIL_GAP = 8;

function panelBottom(index: number): number {
  return RAIL_BOTTOM + index * (BUTTON_SIZE + RAIL_GAP);
}

/**
 * Vertical icon strip pinned to the bottom-left edge of the viewport.
 * Each icon opens a small popover panel right next to itself for one
 * crew domain — mooring lines, anchor, fenders, and the lone sailor
 * who can leave the boat to handle dock-side work. Only one popover
 * is open at a time.
 *
 * Popovers are anchored vertically to the same row as their trigger
 * button — see `panelBottom()` — so the panel feels attached to the
 * icon and never covers more than its own vertical band of the
 * screen.
 */
@Component({
  selector: "po-crew-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MooringDrawerComponent,
    AnchorDrawerComponent,
    FendersDrawerComponent,
    CrewDrawerComponent,
    MastDrawerComponent,
  ],
  templateUrl: "./crew-panel.component.html",
  styleUrl: "./crew-panel.component.css",
})
export class CrewPanelComponent {
  private readonly store = inject(CrewCommandsStore);

  readonly panel = signal<CrewPanel>(null);

  readonly madeFastCount = computed(() => this.store.madeFastCount());
  readonly fendersOutCount = computed(() => this.store.fendersOutCount());
  readonly anchorDown = computed(() => this.store.anchorDown());
  readonly sailorAshore = computed(() => this.store.crewAshore());
  readonly sailorOffBoat = computed(() => this.store.sailorOffBoat());
  readonly sailorStranded = computed(() => this.store.sailorStranded());
  readonly mastFoldable = computed(() => this.store.mastFoldable());
  readonly mastDown = computed(() => this.store.mastDown());

  private static readonly INDEX: Record<Exclude<CrewPanel, null>, number> = {
    mooring: 0,
    anchor: 1,
    fenders: 2,
    crew: 3,
    mast: 4,
  };

  bottomFor(p: Exclude<CrewPanel, null>): number {
    return panelBottom(CrewPanelComponent.INDEX[p]);
  }

  open(p: Exclude<CrewPanel, null>): void {
    this.panel.update((cur) => (cur === p ? null : p));
  }

  close(): void {
    this.panel.set(null);
  }
}
